import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { supabase } from "@/lib/supabase"
import { getSupabaseFunctionErrorMessage } from "@/utils/supabaseFunctionUtils"

export interface CreateDiaryEntryParams {
  personId: string
  notes: string
  driverOpsTag: string
  driverOpsSubTag: string[]
  callDateTime: string
  followUpAction?: string
  followUpComplete?: boolean
}

export const useCreateDiaryEntry = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateDiaryEntryParams) => {
      const { data, error } = await supabase.functions.invoke("create-diary-entry", {
        body: params,
      })

      if (error) {
        console.error("Error creating diary entry:", error)
        // invoke() only reports a non-2xx status, so read the body for the validation detail
        const message = await getSupabaseFunctionErrorMessage(error)
        throw new Error(message || "Failed to create diary entry")
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diaryEntries"] })
      queryClient.invalidateQueries({ queryKey: ["diaryEntries-infinite"] })

      toast.success("Success", {
        description: "Diary entry created successfully",
      })
    },
    onError: (error: Error) => {
      toast.error("Error", {
        description: error.message || "Failed to create diary entry",
      })
    },
  })
}
